import { useQueryClient } from "@tanstack/react-query";

import {
  getGetDealGeneralPartnerViewV3QueryKey,
  useCloseDeal,
  useGetDealCloseSummary,
} from "@vega-api/generated/deal/deal";
import { Spinner } from "@vega-core/components/Loaders/LoadingSpinner/Spinner";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { CloseDealSummary } from "../CloseDealSummary/CloseDealSummary";

export type CloseDealModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
};

export const CloseDealModal = ({
  isOpen,
  onClose,
  dealId,
}: CloseDealModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useGetDealCloseSummary(dealId, {
    query: {
      enabled: isOpen,
      select: (response) => response.data,
    },
  });

  const { mutate: closeDeal, isPending: isClosing } = useCloseDeal({
    mutation: {
      onSuccess: () => {
        showToast({
          variant: "success",
          title: m.status_success(),
          description: m.coInvestments_closeDeal_toast_success(),
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealGeneralPartnerViewV3QueryKey(dealId),
        });
        onClose();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_closeDeal_toast_error(),
        });
      },
    },
  });

  const handleConfirm = () => {
    closeDeal({ id: dealId });
  };

  const renderBody = () => {
    if (isSummaryLoading) {
      return (
        <div className="flex justify-center py-6">
          <Spinner asIcon />
        </div>
      );
    }

    if (isSummaryError || !summary) {
      return (
        <p className="body-base-regular text-text/negative">
          {m.coInvestments_closeDeal_summary_error()}
        </p>
      );
    }

    return <CloseDealSummary summary={summary} />;
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: handleConfirm,
        mainActionText: m.coInvestments_closeDeal_modal_confirmButton(),
        mainActionVariant: "destructive",
        isLoading: isClosing,
        disabled: isClosing || !summary,
        secondaryAction: onClose,
        secondaryActionText: m.btn_cancel(),
        disabledSecondary: isClosing,
      }}
      dataTestId="close-deal-modal"
      description={m.coInvestments_closeDeal_modal_description()}
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_closeDeal_modal_title()}
    >
      {renderBody()}
    </ActionaryModal>
  );
};
